import { useCallback } from "react";

import { useLandlordVerificationModal } from "@/components/contexts/LandlordVerificationContext/useLandlordVerificationModal";
import { useLandlordVerification } from "./useLandlordVerification";

/**
 * Gates landlord actions that require a confirmed identity (an uploaded
 * Emirates ID, or Persona). Identity verification is not required to add a
 * property — it is enforced here, at the actions that legally need it:
 * inviting tenants/agents, Ejari registration, and brokerage approvals.
 *
 * Returns a function that runs `onVerified` when the landlord is verified, and
 * otherwise opens the verification modal in place.
 *
 * Must be used within `LandlordVerificationProvider` (mounted in
 * `LandlordLayout`) — i.e. only from landlord routes.
 */
export const useRequireLandlordVerification = () => {
  const { isVerified } = useLandlordVerification();
  const { openVerification } = useLandlordVerificationModal();

  return useCallback(
    (onVerified: () => void) => {
      if (!isVerified) {
        openVerification();
        return;
      }
      onVerified();
    },
    [isVerified, openVerification],
  );
};
